import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, MapPin } from "lucide-react";
import { getPropertyDetail } from "../services/apiService";
import { getMediaBaseUrl, getClientEntry } from "../config/clientConfig";
import { getSourceName } from "../services/authService";
import { decodeRefNum } from "../utils/refNumCrypto";
import { formatClearlineDateTime } from "../utils/statusMapping";
import BackToCases from "../components/BackToCases";
import { toPropertyDetail } from "./propertyDetail";
import CustomMap from "../components/CustomMap";
import ImageLightbox from "../redesign/components/ImageLightbox";
import PropertySummaryPanel from "./PropertySummaryPanel";

/**
 * A property claim.
 *
 * Its own page rather than a mode of the motor one: that page is panels, damage
 * intensity per panel and a 3D vehicle, none of which a building has. What a
 * property claim has is photographs and the coordinates they were taken at, so
 * that is what this shows.
 */

const card = "rounded-card border border-cl-card bg-cl-surface overflow-hidden";
const cardHead =
  "flex items-center justify-between gap-3 border-b border-cl-head px-4 py-[11px]";
const cardTitle = "text-[13.5px] font-semibold text-cl-text";
const label =
  "text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label";

const mediaUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = getMediaBaseUrl();
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

/**
 * A photograph, or a stated reason there isn't one.
 *
 * The property API returns filesystem paths under /home/camcom/media_staging/,
 * and no host and prefix combination tried so far serves them — so a failure
 * here is expected until the media base is known, and it says so with the URL
 * it tried rather than leaving an empty square.
 */
function Photo({ photo, onOpen }) {
  const [failed, setFailed] = useState(false);
  const url = mediaUrl(photo.picture);

  return (
    <figure className="m-0">
      <button
        type="button"
        onClick={onOpen}
        disabled={failed}
        className="block w-full overflow-hidden rounded-tile border border-cl-card bg-cl-canvas"
      >
        {failed ? (
          <span
            className="flex aspect-[4/3] w-full items-center justify-center px-3 text-center text-[11px] leading-[1.5] text-cl-faint"
            title={url}
          >
            Image did not load — the media host for this path is not resolved
          </span>
        ) : (
          <img
            src={url}
            alt={photo.label}
            loading="lazy"
            className="block aspect-[4/3] w-full object-cover"
            onError={() => {
              console.info("[Property] image failed:", url);
              setFailed(true);
            }}
          />
        )}
      </button>
      <figcaption className="mt-[5px] flex items-baseline justify-between gap-2">
        <span className="truncate text-[12px] font-medium text-cl-text">
          {photo.label}
        </span>
        {photo.lat !== null && photo.longitude !== null && (
          <span className="flex-none text-[10.5px] text-cl-faint">
            {photo.lat.toFixed(5)}, {photo.longitude.toFixed(5)}
          </span>
        )}
      </figcaption>
    </figure>
  );
}

export default function PropertyCaseDetails() {
  const { caseId: encoded } = useParams();
  const refNum = decodeRefNum(encoded);

  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    if (!refNum) return undefined;
    let cancelled = false;

    setState({ loading: true, error: null, data: null });
    getPropertyDetail(refNum)
      .then((raw) => {
        if (!cancelled) {
          setState({ loading: false, error: null, data: toPropertyDetail(raw) });
        }
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error, data: null });
      });

    return () => {
      cancelled = true;
    };
  }, [refNum]);

  const openPhoto = useCallback(
    (index) => setLightbox({ open: true, index }),
    [],
  );

  const { loading, error, data } = state;

  /**
   * The located photographs in the shape CustomMap reads.
   *
   * It wants `created_at`, which the adapter renames to `createdAt` along with
   * every other field — so passing the adapted objects straight in left that
   * one undefined, and its popup called toISOString on an invalid Date and
   * threw out of render. Memoised because CustomMap regroups its markers
   * whenever this array's identity changes.
   */
  const markerPhotos = useMemo(
    () =>
      (data?.located || []).map((photo) => ({
        ...photo,
        created_at: photo.createdAt,
      })),
    [data],
  );

  const closeLightbox = useCallback(
    () => setLightbox({ open: false, index: 0 }),
    [],
  );


  if (loading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-[13px] text-cl-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading property inspection…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8">
        <div className="text-[14px] font-semibold text-cl-text">
          Could not load this property inspection
        </div>
        <p className="mt-1 text-[12.5px] text-cl-muted">
          {error?.message || "The response held no property data."}
        </p>
      </div>
    );
  }

  /**
   * Every photograph as the shared lightbox wants it, in the order the page
   * lays them out — exterior, then interior, then the rest — so its
   * previous/next walks the inspection rather than upload order.
   */
  const frames = data.groups.flatMap((group) =>
    group.photos.map((photo) => ({
      id: photo.id,
      imageUrl: mediaUrl(photo.picture),
      title: `${group.label} · ${photo.label}`,
      side: photo.label,
      alt: photo.label,
    })),
  );

  // photo id -> its position in that list, so a tile opens itself and not the
  // first frame of its group.
  const frameIndex = new Map(frames.map((frame, index) => [frame.id, index]));

  // The summary reports its findings against photographs; clicking one opens
  // that photograph, which is the point of evidence_images.
  const openPhotoById = (photo) => openPhoto(frameIndex.get(photo?.id) ?? 0);

  return (
    <div className="cl-scroll h-full overflow-y-auto px-4 pb-8 pt-4 sm:px-8 sm:pb-10 sm:pt-5">
      {/* Header */}
      {/* Was a hardcoded navigate("/cases"), which sent a reader who came from
          the redesigned list into the old design. It returns to whichever list
          linked here, and shows nothing when the case was opened in a new
          tab. */}
      <BackToCases className="mb-4" />

      <h1 className="text-[22px] font-bold tracking-[-0.01em] text-cl-text">
        {data.address || data.refNum}
      </h1>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-cl-muted">
        <span>Ref {data.refNum}</span>
        {data.createdAt && (
          <span>Received {formatClearlineDateTime(data.createdAt)}</span>
        )}
        {data.userId && <span>By {data.userId}</span>}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-[1fr_348px]">
        {/* Photographs, grouped as the inspection was walked */}
        <div className="flex min-w-0 flex-col gap-4">
          <PropertySummaryPanel
            detail={{
              ...data,
              region: getClientEntry(getSourceName())?.summaryRegion,
            }}
            onOpenPhoto={openPhotoById}
          />

          {data.groups.length === 0 ? (
            <div className={`${card} px-4 py-10 text-center`}>
              <p className="text-[12.5px] text-cl-muted">
                This inspection has no photographs.
              </p>
            </div>
          ) : (
            data.groups.map((group) => (
              <div key={group.side} className={card}>
                <div className={cardHead}>
                  <span className={cardTitle}>{group.label}</span>
                  <span className="text-[11px] text-cl-faint">
                    {group.photos.length} photo
                    {group.photos.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4">
                  {group.photos.map((photo) => (
                    <Photo
                      key={photo.id}
                      photo={photo}
                      onOpen={() => openPhoto(frameIndex.get(photo.id) ?? 0)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Where it was taken */}
        <div className="flex min-w-0 flex-col gap-3">
          <div className={card}>
            <div className={cardHead}>
              <div className="flex items-center gap-2">
                <MapPin className="h-[14px] w-[14px] text-cl-accent" />
                <span className={cardTitle}>Location</span>
              </div>
              <span className="text-[11px] text-cl-faint">
                {data.counts.located} of {data.photos.length} located
              </span>
            </div>

            {data.location ? (
              <>
                <div className="h-[260px] w-full">
                  {/* CustomMap clusters by coordinate and slides through the
                      photographs at each one, which is what these are: many
                      shots from one spot. */}
                  <CustomMap
                    uploaded={markerPhotos}
                    showPolygon={false}
                    setShowPolygon={() => {}}
                    setMaxDistance={() => {}}
                  />
                </div>
                <div className="border-t border-cl-row px-4 py-[11px]">
                  <div className={label}>Coordinates</div>
                  <div className="mt-[3px] font-mono text-[12.5px] text-cl-text">
                    {data.location.lat.toFixed(7)},{" "}
                    {data.location.longitude.toFixed(7)}
                  </div>
                  <p className="mt-[6px] text-[11px] leading-[1.5] text-cl-faint">
                    The point {data.location.photoCount} of{" "}
                    {data.photos.length} photographs share. Handset GPS drifts
                    between shots, so this is a real reading rather than an
                    average of them.
                  </p>
                </div>
              </>
            ) : (
              <p className="px-4 py-8 text-center text-[12.5px] text-cl-muted">
                No photograph carried usable coordinates.
              </p>
            )}
          </div>

          {/* Capture conditions — free with the response, and what says whether
              the photographs can be trusted. */}
          <div className={card}>
            <div className={cardHead}>
              <span className={cardTitle}>Capture</span>
              {data.capture.inspectionMode && (
                <span className="text-[11px] text-cl-faint">
                  {data.capture.inspectionMode}
                </span>
              )}
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 px-4 py-[13px]">
              {[
                ["Photographs", `${data.counts.total}`],
                [
                  "Exterior / interior / other",
                  `${data.counts.exterior} / ${data.counts.interior} / ${data.counts.additional}`,
                ],
                ["Device", data.capture.device],
                ["Operating system", data.capture.os],
                ["Upload took", data.capture.timeTaken],
                [
                  "Connection",
                  data.capture.internet.min && data.capture.internet.max
                    ? `${data.capture.internet.min}–${data.capture.internet.max} Mbps`
                    : data.capture.internet.max
                      ? `${data.capture.internet.max} Mbps peak`
                      : null,
                ],
              ]
                .filter(([, value]) => value)
                .map(([term, value]) => (
                  <div key={term}>
                    <dt className={label}>{term}</dt>
                    <dd className="mt-[2px] text-[12.5px] text-cl-text">
                      {value}
                    </dd>
                  </div>
                ))}
            </dl>
            {data.capture.internet.note && (
              <p className="border-t border-cl-row px-4 py-[10px] text-[11.5px] text-cl-muted">
                {data.capture.internet.note}
              </p>
            )}
          </div>
        </div>
      </div>

      <ImageLightbox
        isOpen={lightbox.open}
        images={frames}
        currentIndex={lightbox.index}
        onClose={closeLightbox}
        // Property photographs carry no annotations, so there is no overlay to
        // toggle and no ROI counterpart to swap in.
        isDamageImages={false}
      />
    </div>
  );
}
